//src/pages/cadastros/ClientCompanyFormPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  HardDrive,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Palette,
  Phone,
  Plus,
  Save,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import {
  useClientCompanyDetail,
  useCreateClientCompany,
  useUpdateClientCompany,
} from "@/hooks/useClientCompanies";
import {
  maskBRPhone,
  maskCEP,
  maskCNPJ,
  normalizeCepToApi,
} from "@/lib/masks";
import { PERMISSIONS } from "@/lib/permissions";
import { BRAZILIAN_STATES, fetchViaCep } from "@/services/viacep";

type ContactKind = "email" | "phone" | "whatsapp" | "other";
type ContactForm = { kind: ContactKind; value: string; is_primary: boolean };

type AddressForm = {
  label: string;
  zip: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  country: string;
  is_primary: boolean;
};

type DeleteTarget =
  | { type: "contact"; index: number }
  | { type: "address"; index: number }
  | null;

const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  phone: "Telefone",
  other: "Outro",
};

const emptyAddress = (): AddressForm => ({
  label: "Principal",
  zip: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  country: "BR",
  is_primary: true,
});

function slugify(value: string) {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function formatContactValue(kind: ContactKind, value: string) {
  if (kind === "phone" || kind === "whatsapp") return maskBRPhone(value);
  return value;
}

function dataUrlToPhotoPayload(dataUrl: string | null) {
  if (!dataUrl?.startsWith("data:")) return null;

  const [meta, base64] = dataUrl.split(",");
  if (!meta || !base64) return null;

  const mimeMatch = meta.match(/^data:(.*?);base64$/);
  const mime = mimeMatch?.[1] || null;
  if (!mime) return null;

  return {
    photo_base64: base64,
    photo_mime_type: mime,
    photo_file_name: "client-company-logo",
  };
}

export default function ClientCompanyFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCreating = !id;
  const isEditing = !!id && location.pathname.endsWith("/editar");
  const isViewing = !!id && !location.pathname.endsWith("/editar");
  const isReadOnly = isViewing;
  const canWriteClientCompanies = user?.permissions?.includes(PERMISSIONS.CLIENT_COMPANIES_WRITE) ?? false;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const detailQuery = useClientCompanyDetail(id);
  const createMut = useCreateClientCompany();
  const updateMut = useUpdateClientCompany();

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    cnpj: "",
    slug: "",
    domain: "",
    state_registration: "",
    municipal_registration: "",
    primary_color: "#4F46E5",
    secondary_color: "#06B6D4",
    timezone: "America/Sao_Paulo",
    locale: "pt-BR",
    is_active: true,
    document_storage_limit_mb: 50,
  });
  const [contacts, setContacts] = useState<ContactForm[]>([
    { kind: "email", value: "", is_primary: true },
  ]);
  const [addresses, setAddresses] = useState<AddressForm[]>([emptyAddress()]);

  useEffect(() => {
    if (!detailQuery.data) return;

    const { client_company, person, contacts: apiContacts, addresses: apiAddresses } = detailQuery.data;

    setForm({
      name: person.name || "",
      legal_name: person.legal_name || "",
      cnpj: person.document_number ? maskCNPJ(person.document_number) : "",
      slug: client_company.slug || "",
      domain: client_company.domain || "",
      state_registration: client_company.state_registration || "",
      municipal_registration: client_company.municipal_registration || "",
      primary_color: client_company.primary_color || "#4F46E5",
      secondary_color: client_company.secondary_color || "#06B6D4",
      timezone: client_company.timezone || "America/Sao_Paulo",
      locale: client_company.locale || "pt-BR",
      is_active: client_company.is_active ?? true,
      document_storage_limit_mb: client_company.document_storage_limit_mb ?? 50,
    });

    setContacts(
      apiContacts.length
        ? apiContacts.map((c) => ({
            kind: c.kind,
            value: c.kind === "phone" || c.kind === "whatsapp" ? maskBRPhone(c.value) : c.value,
            is_primary: c.is_primary,
          }))
        : [{ kind: "email", value: "", is_primary: true }],
    );

    setAddresses(
      apiAddresses.length
        ? apiAddresses.map((a, index) => ({
            label: a.label || "Principal",
            zip: a.zip ? maskCEP(a.zip) : "",
            street: a.street || "",
            number: a.number || "",
            complement: a.complement || "",
            district: a.district || "",
            city: a.city || "",
            state: a.state || "",
            country: a.country || "BR",
            is_primary: a.is_primary || index === 0,
          }))
        : [emptyAddress()],
    );

    if (person.photo?.base64 && person.photo?.mime_type) {
      setLogoPreview(`data:${person.photo.mime_type};base64,${person.photo.base64}`);
    } else {
      setLogoPreview(null);
    }
  }, [detailQuery.data]);

  const busy = detailQuery.isLoading || createMut.isPending || updateMut.isPending;
  const primaryAddressCount = useMemo(
    () => addresses.filter((a) => a.is_primary).length,
    [addresses],
  );

  function goBack() {
    navigate("/cadastros/clientes");
  }

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (isReadOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Use JPG ou PNG.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Máximo 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    if (isReadOnly) return;
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function addContact() {
    if (isReadOnly) return;
    setContacts((prev) => [...prev, { kind: "whatsapp", value: "", is_primary: false }]);
  }

  function removeContact(idx: number) {
    if (isReadOnly) return;
    setContacts((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((c) => c.is_primary)) next[0].is_primary = true;
      return next.length ? next : [{ kind: "email", value: "", is_primary: true }];
    });
  }

  function setPrimaryContact(idx: number) {
    if (isReadOnly) return;
    setContacts((prev) => prev.map((c, i) => ({ ...c, is_primary: i === idx })));
  }

  function updateContact(idx: number, field: "kind" | "value", value: string) {
    if (isReadOnly) return;
    setContacts((prev) => {
      const next = [...prev];
      if (field === "kind") {
        next[idx] = {
          ...next[idx],
          kind: value as ContactKind,
          value: formatContactValue(value as ContactKind, next[idx].value),
        };
      } else {
        next[idx] = {
          ...next[idx],
          [field]: field === "value" ? formatContactValue(next[idx].kind, value) : value,
        };
      }
      return next;
    });
  }

  function addAddress() {
    if (isReadOnly) return;
    setAddresses((prev) => [...prev, { ...emptyAddress(), is_primary: false }]);
  }

  function removeAddress(idx: number) {
    if (isReadOnly) return;
    setAddresses((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((a) => a.is_primary)) next[0].is_primary = true;
      return next.length ? next : [emptyAddress()];
    });
  }

  function setPrimaryAddress(idx: number) {
    if (isReadOnly) return;
    setAddresses((prev) => prev.map((a, i) => ({ ...a, is_primary: i === idx })));
  }

  function setAddressField(idx: number, key: keyof AddressForm, value: string | boolean) {
    if (isReadOnly) return;
    setAddresses((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value } as AddressForm;
      return next;
    });
  }

  async function handleCepBlur(addrIdx: number) {
    if (isReadOnly) return;

    const cep = normalizeCepToApi(addresses[addrIdx]?.zip || "");
    if (!cep || cep.length !== 8) return;

    const data = await fetchViaCep(cep);

    if (!data) {
      toast.info("Não foi possível consultar o CEP agora. Você pode preencher o endereço manualmente.");
      return;
    }

    setAddresses((prev) => {
      const next = [...prev];
      const cur = next[addrIdx] || emptyAddress();

      next[addrIdx] = {
        ...cur,
        zip: maskCEP(data.cep || cur.zip),
        street: data.logradouro || cur.street,
        complement: data.complemento || cur.complement,
        district: data.bairro || cur.district,
        city: data.localidade || cur.city,
        state: (data.uf || cur.state).toUpperCase(),
        country: "BR",
      };

      return next;
    });
  }

  function confirmDeleteTarget() {
    if (!deleteTarget || isReadOnly) return;

    if (deleteTarget.type === "contact") {
      removeContact(deleteTarget.index);
      toast.success("Contato removido");
    } else {
      removeAddress(deleteTarget.index);
      toast.success("Endereço removido");
    }

    setDeleteTarget(null);
  }

  async function handleSave() {
    if (isReadOnly || !canWriteClientCompanies) return;

    if (!form.name.trim()) {
      toast.error("Informe o nome do cliente");
      return;
    }

    if (!form.legal_name.trim()) {
      toast.error("Informe a razão social");
      return;
    }

    if (!form.cnpj.trim()) {
      toast.error("Informe o CNPJ");
      return;
    }

    const validAddresses = addresses.filter((a) =>
      [a.zip, a.street, a.number, a.district, a.city, a.state].some((v) => (v || "").trim()),
    );

    if (validAddresses.length === 0) {
      toast.error("Informe ao menos um endereço");
      return;
    }

    if (primaryAddressCount === 0) {
      toast.error("Defina um endereço principal");
      return;
    }

    const body = {
      person: {
        person_type: "PJ" as const,
        name: form.name.trim(),
        legal_name: form.legal_name.trim(),
        document_number: form.cnpj || null,
        photo: dataUrlToPhotoPayload(logoPreview),
        contacts: contacts
          .filter((c) => c.value?.trim())
          .map((c) => ({
            kind: c.kind,
            value: c.value.trim(),
            is_primary: c.is_primary,
          })),
        addresses: validAddresses.map((a) => ({
          label: a.label || null,
          zip: a.zip || null,
          street: a.street || null,
          number: a.number || null,
          complement: a.complement || null,
          district: a.district || null,
          city: a.city || null,
          state: a.state || null,
          country: a.country || "BR",
          is_primary: a.is_primary,
        })),
      },
      slug: slugify(form.slug || form.name),
      domain: form.domain || null,
      state_registration: form.state_registration || null,
      municipal_registration: form.municipal_registration || null,
      primary_color: form.primary_color,
      secondary_color: form.secondary_color,
      timezone: form.timezone,
      locale: form.locale,
      is_active: form.is_active,
      document_storage_limit_mb: Number(form.document_storage_limit_mb || 50),
    };

    try {
      if (isCreating) {
        const result = await createMut.mutateAsync(body);
        toast.success("Cliente cadastrado com sucesso");
        navigate(`/cadastros/clientes/${result.id}`);
      } else if (id && isEditing) {
        await updateMut.mutateAsync({ id, body });
        toast.success("Cliente atualizado com sucesso");
        navigate(`/cadastros/clientes/${id}`);
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Erro ao salvar cliente");
    }
  }

  if (detailQuery.isLoading && id) {
    return <Card className="p-6 text-sm text-muted-foreground">Carregando cliente...</Card>;
  }

  if (detailQuery.isError && id) {
    return (
      <Card className="p-6">
        <div className="text-sm text-destructive">
          {detailQuery.error instanceof Error ? detailQuery.error.message : "Erro ao carregar cliente"}
        </div>
        <Button variant="outline" className="mt-4" onClick={goBack}>
          Voltar
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Button variant="ghost" className="mb-2 px-0" onClick={goBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">
            {isCreating ? "Novo Cliente" : isEditing ? "Editar Cliente" : "Detalhes do Cliente"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Cadastro de identificação, endereço e contatos do Cliente da Empresa.
          </p>
        </div>

        <div className="flex gap-2">
          {isViewing && canWriteClientCompanies ? (
            <Button onClick={() => navigate(`/cadastros/clientes/${id}/editar`)}>
              Editar
            </Button>
          ) : null}

          {!isReadOnly ? (
            <Button onClick={handleSave} disabled={busy || !canWriteClientCompanies}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-primary" />
            Dados do Cliente
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-col gap-6 lg:flex-row">
            <div className="w-full shrink-0 lg:w-56">
              <Label className="mb-2 block">Logo</Label>

              <div className="rounded-xl border border-dashed p-3">
                <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="h-full w-full object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <ImageIcon className="h-8 w-8" />
                      <span className="px-2 text-center text-xs">JPG ou PNG até 2MB</span>
                    </div>
                  )}
                </div>

                {!isReadOnly ? (
                  <div className="mt-3 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isReadOnly}
                    >
                      <ImageIcon className="mr-2 h-4 w-4" />
                      {logoPreview ? "Trocar" : "Enviar"}
                    </Button>

                    {logoPreview ? (
                      <Button type="button" variant="ghost" size="icon" onClick={removeLogo}>
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={handleLogoUpload}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Nome do Cliente *</Label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                      slug: prev.slug ? prev.slug : slugify(e.target.value),
                    }))
                  }
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>Razão Social *</Label>
                <Input
                  value={form.legal_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, legal_name: e.target.value }))}
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>CNPJ *</Label>
                <Input
                  value={form.cnpj}
                  onChange={(e) => setForm((prev) => ({ ...prev, cnpj: maskCNPJ(e.target.value) }))}
                  placeholder="00.000.000/0001-00"
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>Slug</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>Domínio</Label>
                <Input
                  value={form.domain}
                  onChange={(e) => setForm((prev) => ({ ...prev, domain: e.target.value }))}
                  className="mt-1"
                  placeholder="cliente.com.br"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>Inscrição Estadual</Label>
                <Input
                  value={form.state_registration}
                  onChange={(e) => setForm((prev) => ({ ...prev, state_registration: e.target.value }))}
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>Inscrição Municipal</Label>
                <Input
                  value={form.municipal_registration}
                  onChange={(e) => setForm((prev) => ({ ...prev, municipal_registration: e.target.value }))}
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <div>
                <Label>Fuso Horário</Label>
                <Select
                  value={form.timezone}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, timezone: value }))}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Sao_Paulo">America/Sao_Paulo</SelectItem>
                    <SelectItem value="America/Manaus">America/Manaus</SelectItem>
                    <SelectItem value="America/Fortaleza">America/Fortaleza</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Idioma</Label>
                <Select
                  value={form.locale}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, locale: value }))}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    <SelectItem value="en-US">English (US)</SelectItem>
                    <SelectItem value="es-ES">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4 text-primary" />
            Branding e Preferências
          </CardTitle>
        </CardHeader>

        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Cor Primária</Label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) => setForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                className="h-10 w-14 cursor-pointer rounded border"
                disabled={isReadOnly}
              />
              <Input
                value={form.primary_color}
                onChange={(e) => setForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div>
            <Label>Cor Secundária</Label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={form.secondary_color}
                onChange={(e) => setForm((prev) => ({ ...prev, secondary_color: e.target.value }))}
                className="h-10 w-14 cursor-pointer rounded border"
                disabled={isReadOnly}
              />
              <Input
                value={form.secondary_color}
                onChange={(e) => setForm((prev) => ({ ...prev, secondary_color: e.target.value }))}
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div>
            <Label>Limite de Documentos (MB)</Label>
            <div className="relative mt-1">
              <HardDrive className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                min={1}
                max={102400}
                value={form.document_storage_limit_mb}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    document_storage_limit_mb: Number(e.target.value || 50),
                  }))
                }
                className="pl-8"
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border p-4">
            <div>
              <Label>Status do Cliente</Label>
              <p className="text-xs text-muted-foreground">
                Clientes inativos ficam indisponíveis para seleção operacional.
              </p>
            </div>
            <Switch
              checked={form.is_active}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_active: checked }))}
              disabled={isReadOnly}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4 text-primary" />
            Contatos
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          {contacts.map((contact, idx) => (
            <div key={idx} className="grid grid-cols-1 gap-3 rounded-xl border p-3 md:grid-cols-[180px_1fr_auto_auto] md:items-end">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={contact.kind}
                  onValueChange={(value) => updateContact(idx, "kind", value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTACT_KIND_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Contato</Label>
                <Input
                  value={contact.value}
                  onChange={(e) => updateContact(idx, "value", e.target.value)}
                  className="mt-1"
                  disabled={isReadOnly}
                />
              </div>

              <Button
                type="button"
                variant={contact.is_primary ? "default" : "outline"}
                onClick={() => setPrimaryContact(idx)}
                disabled={isReadOnly}
              >
                <Star className="mr-2 h-4 w-4" />
                Principal
              </Button>

              {!isReadOnly ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteTarget({ type: "contact", index: idx })}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              ) : null}
            </div>
          ))}

          {!isReadOnly ? (
            <Button type="button" variant="outline" onClick={addContact}>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Contato
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4 text-primary" />
            Endereços
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {addresses.map((address, idx) => (
            <div key={idx} className="space-y-4 rounded-xl border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium">{address.label || `Endereço ${idx + 1}`}</div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant={address.is_primary ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPrimaryAddress(idx)}
                    disabled={isReadOnly}
                  >
                    <Star className="mr-2 h-4 w-4" />
                    Principal
                  </Button>

                  {!isReadOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget({ type: "address", index: idx })}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <Label>Rótulo</Label>
                  <Input
                    value={address.label}
                    onChange={(e) => setAddressField(idx, "label", e.target.value)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>CEP</Label>
                  <Input
                    value={address.zip}
                    onChange={(e) => setAddressField(idx, "zip", maskCEP(e.target.value))}
                    onBlur={() => void handleCepBlur(idx)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Logradouro</Label>
                  <Input
                    value={address.street}
                    onChange={(e) => setAddressField(idx, "street", e.target.value)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Número</Label>
                  <Input
                    value={address.number}
                    onChange={(e) => setAddressField(idx, "number", e.target.value)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Complemento</Label>
                  <Input
                    value={address.complement}
                    onChange={(e) => setAddressField(idx, "complement", e.target.value)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Bairro</Label>
                  <Input
                    value={address.district}
                    onChange={(e) => setAddressField(idx, "district", e.target.value)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Cidade</Label>
                  <Input
                    value={address.city}
                    onChange={(e) => setAddressField(idx, "city", e.target.value)}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>UF</Label>
                  <Select
                    value={address.state || ""}
                    onValueChange={(value) => setAddressField(idx, "state", value)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAZILIAN_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}

          {!isReadOnly ? (
            <Button type="button" variant="outline" onClick={addAddress}>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Endereço
            </Button>
          ) : null}
        </CardContent>
      </Card>


      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remover item"
        description="Deseja remover este item do cadastro?"
        onConfirm={confirmDeleteTarget}
        confirmLabel="Remover"
        variant="destructive"
      />
    </div>
  );
}
